import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  Injector,
  Input,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NgControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { splitDateTimeToParts } from '../../utils/date.utils';

type DatetimeValue = Date | string | null;

/**
 * Составной CVA-компонент «дата + время» (MatDatepicker + <input type="time">),
 * объединённый в одну `FormControl<Date | null>`.
 *
 * Принимает `Date | string | null` (для удобства миграции со строк API).
 * Отдаёт `Date | null` через `onChange`.
 *
 * Ошибки валидации читаются с родительского `NgControl` (`required`,
 * `dateRange`, `min`, `max`, `serverError`) и показываются на date-пикере;
 * время подсвечивается только для `required` (как в исходных формах).
 */
@Component({
  selector: 'app-datetime-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
  ],
  templateUrl: './datetime-input.component.html',
  styleUrl: './datetime-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DatetimeInputComponent),
    },
  ],
})
export class DatetimeInputComponent implements ControlValueAccessor, OnInit {
  // ── Лейблы (требование пользователя) ──────────────────────────────
  @Input() dateLabel = 'Дата';
  @Input() timeLabel = 'Время';

  // ── Стандартные CVA-inputs ────────────────────────────────────────
  @Input() required = false;

  // ── Опционально ───────────────────────────────────────────────────
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;
  @Input() step = 300;
  @Input() placeholder = '';
  @Input() dateErrorMessage = 'Укажите дату';
  @Input() timeErrorMessage = 'Укажите время';
  @Input() dateRangeErrorMessage = 'Дата окончания должна быть не раньше даты начала';
  @Input() minErrorMessage = 'Дата вне допустимого диапазона';
  @Input() maxErrorMessage = 'Дата вне допустимого диапазона';

  // ── Внутренние контролы (без валидаторов — они на родителе) ───────
  readonly dateControl = new FormControl<Date | null>(null);
  readonly timeControl = new FormControl<string>('00:00');
  readonly internal = new FormGroup({
    date: this.dateControl,
    time: this.timeControl,
  });

  // `onTouched` используется в шаблоне `(blur)="onTouched()"` — Angular
  // strict-templates требует, чтобы такие свойства были не `private`.
  // Саму функцию перезаписывает Angular Forms через `registerOnTouched`.
  onChange: (v: Date | null) => void = () => {};
  onTouched: () => void = () => {};

  private readonly injector = inject(Injector);
  private ngControl: NgControl | null = null;

  private get parentErrors(): Record<string, any> | null {
    const control = this.ngControl?.control as FormControl | null;
    return control?.errors ?? null;
  }

  ngOnInit(): void {
    // Отложенное получение NgControl — разрывает цикл DI.
    // К моменту ngOnInit компонент уже создан, и NgControl уже привязан
    // к этому компоненту через NG_VALUE_ACCESSOR-провайдер.
    // Нам NgControl нужен только для чтения parentErrors (для отображения ошибок валидации).
    try {
      this.ngControl = this.injector.get(NgControl, null, { self: true });
    } catch {
      this.ngControl = null;
    }
  }

  constructor() {
    // Любое изменение внутри — пересобираем Date|null и пушим наверх
    this.internal.valueChanges.subscribe(() => {
      this.onChange(this.combinePartsToDate());
    });
    this.dateControl.valueChanges.subscribe(() => this.onTouched());
    this.timeControl.valueChanges.subscribe(() => this.onTouched());
  }

  // ── CVA-методы ────────────────────────────────────────────────────
  writeValue(value: DatetimeValue): void {
    if (value === null || value === undefined) {
      this.setInternal(null, '00:00');
      return;
    }
    if (value instanceof Date) {
      if (isNaN(value.getTime())) {
        this.setInternal(null, '00:00');
        return;
      }
      this.setInternal(
        new Date(value.getFullYear(), value.getMonth(), value.getDate()),
        `${this.pad2(value.getHours())}:${this.pad2(value.getMinutes())}`,
      );
      return;
    }
    // string — переиспользуем splitDateTimeToParts
    const parts = splitDateTimeToParts(value);
    this.setInternal(parts?.date ?? null, parts?.time ?? '00:00');
  }

  registerOnChange(fn: (v: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.internal.disable({ emitEvent: false });
    } else {
      this.internal.enable({ emitEvent: false });
    }
  }

  // ── View helpers (для шаблона) ────────────────────────────────────
  showDateError(): boolean {
    const errors = this.parentErrors;
    if (!errors) return false;
    return !!(
      errors['required'] ||
      errors['dateRange'] ||
      errors['min'] ||
      errors['max'] ||
      errors['serverError']
    );
  }

  currentDateErrorMessage(): string {
    const errors = this.parentErrors;
    if (!errors) return '';
    if (errors['required']) return this.dateErrorMessage;
    if (errors['dateRange']) return this.dateRangeErrorMessage;
    if (errors['min']) return this.minErrorMessage;
    if (errors['max']) return this.maxErrorMessage;
    if (errors['serverError']) return String(errors['serverError']);
    return '';
  }

  showTimeError(): boolean {
    if (!this.required) return false;
    return !!(this.parentErrors?.['required'] && (this.dateControl.touched || this.timeControl.touched));
  }

  // ── Внутренние утилиты ────────────────────────────────────────────
  private combinePartsToDate(): Date | null {
    const date = this.dateControl.value;
    if (!date) return null;
    const timeRaw = this.timeControl.value ?? '00:00';
    const t = /^\d{2}:\d{2}/.test(timeRaw) ? timeRaw : '00:00';
    const [hh, mi] = t.split(':').map(Number);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hh, mi);
  }

  private setInternal(date: Date | null, time: string): void {
    this.dateControl.patchValue(date, { emitEvent: false });
    this.timeControl.patchValue(time, { emitEvent: false });
    // Ручной push — родитель ждёт актуальное значение после writeValue
    this.onChange(this.combinePartsToDate());
  }

  private pad2(n: number): string {
    return String(n).padStart(2, '0');
  }
}
